import { redirectTilInnsendtSkjemaUrl, redirectTilLagretSkjemaPaaDokumenterUrl } from 'src/constants/apiConsts';
import { SKJEMA_URL } from 'src/constants/constants';
import { getQueryParams } from 'src/util';

import type { FetchError } from './types';
import type { RootState } from '../reducers';
import type { Bundle, BundleEntry, Questionnaire, QuestionnaireResponse } from 'fhir/r4';
import type { CreateSkjemasvarRequest, UpdateSkjemasvarRequest } from 'src/types/MinHelseEntities';
import type { LanguageLocales } from 'src/util/portalLanguage-utils';

import type { HelsenorgeHttpError, HelsenorgeProxyError, ProxyErrorResponse } from '@helsenorge/framework-utils/hn-proxy-service';
import { HNeventSetDriftsmeldingPath } from '@helsenorge/framework-utils/web-component/events';
import {
  generateQuestionnaireResponse,
  getQuestionnaireItemsWithType,
  getResponseItemAndPathWithLinkId,
  ItemTypeConstants,
} from '@helsenorge/refero';

export const isNewDocument = (state: RootState): boolean => {
  return state.refero.ui.userMetadata?.IsNewDocument ?? false;
};
export function getFilteredParams(): string {
  const params = getQueryParams();
  params.delete('Query');

  return params.toString();
}

export const createQuestionnaireResponse = (state: RootState): QuestionnaireResponse => {
  return {
    status: 'in-progress',
    resourceType: 'QuestionnaireResponse',
    ...state.refero.form.FormData.Content,
    questionnaire: state.refero.form.FormDefinition.Content?.url,
  };
};
export function getQuestionnaireInLanguage(questionnaires: Array<Questionnaire>, language: LanguageLocales): Questionnaire | undefined {
  const skjemaDefinition = questionnaires.filter(q => q.language?.toLowerCase() === language?.toLowerCase());
  return skjemaDefinition.length > 0 ? skjemaDefinition[0] : undefined;
}

export function getLoadParameter(query: string, skjemaNavnParam: string | undefined): [string, string] {
  if (skjemaNavnParam) {
    return ['name', skjemaNavnParam];
  }
  const nameMatchString = 'Questionnaire?name=';
  const idMatchString = 'Questionnaire/';
  const hasNameParam = decodeURIComponent(query).indexOf(nameMatchString) > -1;
  return hasNameParam
    ? ['name', decodeURIComponent(query).substr(nameMatchString.length)]
    : ['id', decodeURIComponent(query).substr(idMatchString.length)];
}

export const createSavePayload = (
  qr: string,
  state: RootState,
  automatiskLagring: boolean,
  isMicrowebStep?: boolean,
  dokumentGuid?: string
): UpdateSkjemasvarRequest | CreateSkjemasvarRequest => {
  const taskId = state.refero.ui.taskId;
  const taskIdMicroweb = state.microweb.taskId;
  const taskIdTouse = taskIdMicroweb ? taskIdMicroweb : taskId;

  const dokumentGuidVanlig = state.refero.ui.dokumentGuid;
  const dokumentGuidMicroweb = state.microweb.documentGuid;
  let dokumentGuidToUse = dokumentGuidMicroweb ? dokumentGuidMicroweb : dokumentGuidVanlig;

  const skalMicrowebStepOverskriveEksisterendeDokument = isMicrowebStep && dokumentGuid;
  if (skalMicrowebStepOverskriveEksisterendeDokument) {
    dokumentGuidToUse = dokumentGuid;
  }

  const payload = {
    ...(dokumentGuidToUse && { DokumentGuid: dokumentGuidToUse }),
    ...(taskIdTouse && { OppgaveGuid: taskIdTouse }),
    AutomatiskLagring: automatiskLagring,
    QuestionnaireResponse: qr,
    ...(skalMicrowebStepOverskriveEksisterendeDokument && { LageNyttDokument: true }),
  };

  return payload;
};

export function getNewUrl(tekniskNavn: string): string {
  return `${SKJEMA_URL}/${tekniskNavn}`;
}

export const gotoUrl = (url: string): void => {
  window.location.assign(url);
};

export const redirectTilLagretSkjemaPaaDokumenter = (id?: string): void => {
  gotoUrl(redirectTilLagretSkjemaPaaDokumenterUrl(id));
};

export const redirectTilInnsendtSkjema = (id: string): void => {
  gotoUrl(redirectTilInnsendtSkjemaUrl(id));
};

export const gotoMinHelse = (): void => {
  gotoUrl('/');
};

export const getQuestionnairs = (parsedData: Questionnaire | Bundle | undefined): Questionnaire[] | undefined => {
  if (!parsedData) {
    return;
  }
  if (parsedData.resourceType === 'Bundle' && parsedData.total === 0) {
    return;
  }
  if (parsedData.resourceType === 'Bundle') {
    return parsedData.entry
      ?.map((entry: BundleEntry) => {
        return entry.resource;
      })
      .filter((x): x is Questionnaire => x?.resourceType === 'Questionnaire');
  } else {
    return [parsedData];
  }
};

export const postUrl = (
  url: string,
  params: {
    [key: string]: string | undefined;
    contextParameter?: string;
    dokumentGuid?: string;
  }
): void => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;

  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const hiddenField = document.createElement('input');
      hiddenField.type = 'hidden';
      hiddenField.name = key;
      hiddenField.value = params[key] || '';

      form.appendChild(hiddenField);
    }
  }

  document.body.appendChild(form);
  form.submit();
};

export const updateUrlAndFetchMessage = (mainQuestionnaire: Questionnaire): void => {
  const filteredParams = getFilteredParams();
  const filteredParamsUrl = filteredParams ? `?${filteredParams}` : '';
  const newUrlPath = getNewUrl(mainQuestionnaire.name || '').replace(/\/+/g, '/');
  window.history.replaceState(window.history.state, '', `/${newUrlPath}${filteredParamsUrl}`);
  HNeventSetDriftsmeldingPath(encodeURIComponent(newUrlPath));
};

export const removeAttachmentsIfNeeded = (
  vedleggMaFjernes: boolean,
  qr: QuestionnaireResponse | undefined,
  mainQuestionnaire: Questionnaire
): void => {
  if (vedleggMaFjernes) {
    const itemsWithType = getQuestionnaireItemsWithType(ItemTypeConstants.ATTATCHMENT, mainQuestionnaire.item);

    itemsWithType?.forEach(itemWithType => {
      qr?.item?.forEach(responseItem => {
        const itemsWithPath = getResponseItemAndPathWithLinkId(itemWithType.linkId, responseItem);
        itemsWithPath.forEach(ip => delete ip.item.answer);
      });
    });
  }
};

export const generateQuestionnaireResponseData = (
  questionnaireResponse: string | undefined,
  mainQuestionnaire: Questionnaire,
  isMicroweb: boolean | undefined,
  skjemadata: string | undefined
): QuestionnaireResponse | undefined => {
  if (isMicroweb && skjemadata !== undefined) {
    const parseOuterObject = JSON.parse(skjemadata);
    return parseOuterObject.Content as QuestionnaireResponse;
  }
  return questionnaireResponse ? JSON.parse(questionnaireResponse) : generateQuestionnaireResponse(mainQuestionnaire);
};

export function isFetchError(obj: unknown): obj is FetchError {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'message' in obj &&
    typeof (obj as FetchError).message === 'string' &&
    ('name' in obj ? typeof (obj as FetchError).name === 'string' : true) &&
    ('statusCode' in obj ? typeof (obj as FetchError).statusCode === 'number' : true) &&
    ('correlationId' in obj ? typeof (obj as FetchError).correlationId === 'string' || (obj as FetchError).correlationId === null : true) &&
    ('response' in obj ? typeof (obj as FetchError).response !== 'undefined' : true) &&
    ('type' in obj ? ['unknown', 'HelsenorgeProxyError', 'HelsenorgeHttpError', 'Error'].includes((obj as FetchError).type!) : true)
  );
}
export const isHelsenorgeProxyError = (error: FetchError): error is HelsenorgeProxyError => {
  return error.type === 'HelsenorgeProxyError';
};
export const isHelsenorgeHttpError = (error: FetchError): error is HelsenorgeHttpError => {
  return error.type === 'HelsenorgeHttpError';
};
export function isProxyErrorResponse(error: unknown): error is ProxyErrorResponse {
  if (typeof error === 'object' && error !== null && 'message' in error && 'code' in error && 'errorCategory' in error) {
    const e = error as { message: unknown; code: unknown; errorCategory: unknown };
    return typeof e.message === 'string' && typeof e.code === 'string' && typeof e.errorCategory === 'number';
  }
  return false;
}
const searchParamsToObject = (searchParams: URLSearchParams): { [key: string]: string | undefined | Array<string> | unknown } => {
  const obj: { [key: string]: string | undefined | Array<string> | unknown } = {};
  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    obj[key] = values.length > 1 ? values : values[0];
  }
  return obj;
};
export function getContextQueryParametersAsJson(ContextParametersMap: URLSearchParams | undefined):
  | {
      [key: string]: string | undefined | Array<string> | unknown;
    }
  | undefined {
  if (!ContextParametersMap) {
    return undefined;
  }
  const dataObject = searchParamsToObject(ContextParametersMap);
  for (const key in dataObject) {
    if (Object.prototype.hasOwnProperty.call(dataObject, key)) {
      if (!key.startsWith('context-') && !key.startsWith('Context-')) {
        delete dataObject[key];
      } else {
        const newKey = key.replace(/^context-/, '').replace(/^Context-/, '');
        if (newKey !== key) {
          dataObject[newKey] = dataObject[key];
          delete dataObject[key];
        }
      }
    }
  }
  return dataObject;
}

export function hasContextParameters(querystring: URLSearchParams | undefined): boolean {
  if (!querystring) {
    return false;
  }

  return querystring.toString().includes('context-') || querystring.toString().includes('Context-');
}

export const bundleQuestionnaires = (questionnaires: Questionnaire | Questionnaire[], existingBundle?: Bundle): Bundle => {
  // 1. Normalize input to an array
  const inputs = Array.isArray(questionnaires) ? questionnaires : [questionnaires];

  // 2. Initialize or clone the Bundle
  const bundle: Bundle = existingBundle
    ? structuredClone(existingBundle)
    : {
        resourceType: 'Bundle',
        type: 'searchset',
        entry: [],
      };

  if (!bundle.entry) {
    bundle.entry = [];
  }

  // 3. Process each questionnaire
  inputs.forEach(q => {
    if (!q.id) {
      // eslint-disable-next-line no-console
      console.warn('Skipping Questionnaire without ID. FHIR resources in a Bundle should have an ID.');
      return;
    }

    // Create the new Entry
    const newEntry: BundleEntry = {
      resource: q,
    };

    // 4. Smart Merge
    const existingIndex =
      bundle.entry?.findIndex(entry => entry.resource?.resourceType === 'Questionnaire' && entry.resource?.id === q.id) || 0;

    if (existingIndex >= 0) {
      bundle.entry![existingIndex] = newEntry;
    } else {
      // Insert: Add new entry
      bundle.entry?.push(newEntry);
    }
  });

  bundle.total = bundle.entry.length;

  return bundle;
};
